import { X } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
	color?:
		| "default"
		| "primary"
		| "success"
		| "warning"
		| "error"
		| "info"
		| "red"
		| "blue"
		| "green"
		| "orange"
		| "purple"
		| "cyan"
		| "pink"
		| "geekblue"
		| "magenta"
		| "volcano"
		| "gold"
		| "lime"
		| "processing";
	closable?: boolean;
	onClose?: (e: React.MouseEvent<HTMLElement>) => void;
	bordered?: boolean;
	icon?: React.ReactNode;
}

const Tag = forwardRef<HTMLSpanElement, TagProps>(
	(
		{
			className,
			color = "default",
			closable = false,
			onClose,
			bordered = true,
			icon,
			children,
			...props
		},
		ref,
	) => {
		const colorStyles: Record<string, string> = {
			default:
				"bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
			primary:
				"bg-primary-50 text-primary-600 border-primary-200 dark:bg-primary-900/30 dark:text-primary-400 dark:border-primary-800",
			success:
				"bg-success-50 text-success-600 border-success-200 dark:bg-success-900/30 dark:text-success-400 dark:border-success-800",
			warning:
				"bg-warning-50 text-warning-600 border-warning-200 dark:bg-warning-900/30 dark:text-warning-400 dark:border-warning-800",
			error:
				"bg-error-50 text-error-600 border-error-200 dark:bg-error-900/30 dark:text-error-400 dark:border-error-800",
			info: "bg-info-50 text-info-600 border-info-200 dark:bg-info-900/30 dark:text-info-400 dark:border-info-800",
			// 扩展颜色
			red: "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
			blue: "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
			green:
				"bg-green-50 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
			orange:
				"bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
			purple:
				"bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
			cyan: "bg-cyan-50 text-cyan-600 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800",
			pink: "bg-pink-50 text-pink-600 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800",
			geekblue:
				"bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
			magenta:
				"bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800",
			volcano:
				"bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
			gold: "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
			lime: "bg-lime-50 text-lime-700 border-lime-200 dark:bg-lime-900/30 dark:text-lime-400 dark:border-lime-800",
			processing:
				"bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800 animate-pulse",
		};

		const handleClose = (e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			onClose?.(e);
		};

		return (
			<span
				ref={ref}
				className={cn(
					"inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded",
					bordered && "border",
					colorStyles[color] || colorStyles.default,
					className,
				)}
				{...props}
			>
				{icon && <span className="flex-shrink-0">{icon}</span>}
				{children}
				{closable && (
					<button
						type="button"
						onClick={handleClose}
						className="ml-1 hover:opacity-70 focus:outline-none"
					>
						<X className="h-3 w-3" />
					</button>
				)}
			</span>
		);
	},
);

Tag.displayName = "Tag";

export { Tag };
